import React, { useState, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import Link from "@/utils/link";

import { Tab } from "@headlessui/react";
import { useAuth } from "@/lib/auth";
import { readDocument, updateDocument } from "@/lib/db";

import Button from "@/ui/Button";
import Checkbox from "@/ui/Checkbox";
import TextField from "@/ui/TextField";

import CommunityCard from "../home/CommunityCard";
import HomeHeader from "@/components/home/HomeHeader";
import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

const SET_USER = "SET_USER";
const UPDATE_USER = "UPDATE_USER";
const UPDATE_KEY_VALUE = "UPDATE_KEY_VALUE";

const reducer = (state, action) => {
    switch (action.type) {
        case SET_USER:
            return { ...state, user: action.user };

        case UPDATE_KEY_VALUE:
            return { ...state, user: { ...state.user, [action.key]: action.value } };

        default:
            return state;
    }
};

const tabs = ["My Favorite Communities", "My Assisted Living Tools"];

const MyAccountPage = ({ tools = false }) => {
    const [state, dispatch] = useReducer(reducer, { user: {} });
    const auth = useAuth();
    const [saving, setSaving] = useState(false);

    const userId = auth.user ? auth.user.uid : "";

    useEffect(() => {
        const getUser = async (uid) => {
            const user = await readDocument("users", uid);
            dispatch({ type: SET_USER, user });
        };

        if (userId) {
            getUser(userId);
        }
    }, [userId]);

    const handleSave = async () => {
        setSaving(true);
        await updateDocument("users", state.user.uid, state.user);
        setSaving(false);
    };

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />

            <div className="pt-24 sm:pt-32 pb-16 container mx-auto px-4">
                {/*<h2 className="mt-8 text-2xl text-gray-med font-headline font-bold uppercase">
                    For Seniors + Their Families
                </h2>*/}

                <div className="container mx-auto my-8">
                    <h2 className="text-2xl text-gray-med font-headline font-bold uppercase">
                        My Assisted Living Tools
                    </h2>
                    <div className="mt-4 px-8 py-4 bg-gray-200/60 rounded">
                        <MyRightFitTools user={state.user} />
                    </div>
                </div>

                <h1 className="mt-12 text-2xl font-headline font-bold text-primary-500 uppercase">
                    My Account
                </h1>

                <div className="mt-4 px-8 py-4 bg-gray-200/60 rounded">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-32 gap-y-8">
                        <div className="sm:w-96">
                            <TextField
                                id="name"
                                name="name"
                                label="Name"
                                value={state.user.name}
                                onChange={(e) =>
                                    dispatch({
                                        type: UPDATE_KEY_VALUE,
                                        key: "name",
                                        value: e.target.value,
                                    })
                                }
                                fullwidth
                                inputClassName="bg-gray-100"
                            />

                            <TextField
                                id="email"
                                type="email"
                                name="email"
                                label="Email"
                                value={state.user.email}
                                onChange={() => {}}
                                fullwidth
                                disabled
                                className="mt-4"
                            />

                            <TextField
                                id="phone"
                                name="phone"
                                label="Mobile Number"
                                value={state.user.phone}
                                onChange={(e) =>
                                    dispatch({
                                        type: UPDATE_KEY_VALUE,
                                        key: "phone",
                                        value: e.target.value,
                                    })
                                }
                                fullwidth
                                inputClassName="bg-gray-100"
                                className="mt-4"
                            />
                        </div>

                        <div className="mt-4">
                            <h2 className="text-2xl text-primary-500 font-headline font-bold">
                                Email Preferences
                            </h2>

                            <p>
                                What types of communication would you like to receive from
                                Purple Door Finders?
                            </p>

                            <div className="mt-4 space-y-2">
                                <Checkbox
                                    label="Search and property related updates"
                                    value={state.user.searchAndPropertyUpdates}
                                    onChange={(e) =>
                                        dispatch({
                                            type: UPDATE_KEY_VALUE,
                                            key: "searchAndPropertyUpdates",
                                            value: state.user.searchAndPropertyUpdates
                                                ? false
                                                : true,
                                        })
                                    }
                                />
                                <Checkbox
                                    label="Senior living updates and newsletter"
                                    value={state.user.seniorLivingAndNewsletterUpdates}
                                    onChange={(e) =>
                                        dispatch({
                                            type: UPDATE_KEY_VALUE,
                                            key: "seniorLivingAndNewsletterUpdates",
                                            value: state.user
                                                .seniorLivingAndNewsletterUpdates
                                                ? false
                                                : true,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex justify-between">
                        <Button onClick={handleSave} disabled={saving}>
                            Update
                        </Button>

                        <Button variant="outlined">Delete Account</Button>
                    </div>
                </div>
            </div>

            <div className="z-10">
                <SeoLinks />
                <HomeFooter />
            </div>
        </div>
    );
};

const MyFavoriteCommunities = () => {
    const [communities, setCommunities] = useState([]);
    const { favourites } = useSelector((state) => state.local);

    console.log(favourites);

    const activeFavorites = Object.keys(favourites).filter((nameId) => favourites[nameId]);
    const searchBody = JSON.stringify({ nameIds: activeFavorites });

    useEffect(() => {
        const fetchData = async () => {
            const res = await fetch("/api/search", {
                method: "POST",
                body: searchBody,
            });

            const data = await res.json();

            if (data.data && data.data.communitys) {
                setCommunities(data.data.communitys);
            } else {
                setCommunities([]);
            }
        };

        fetchData();
    }, [searchBody]);

    return (
        <div className="pb-16">
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-8">
                {communities.map((community) => (
                    <CommunityCard key={community.nameId} community={community} />
                ))}
            </div>

            {communities.length === 0 ? (
                <div className="mt-4">
                    <p className="text-lg">
                        You don't have any favorite communities yet.
                        <br />
                        Search for communities and click on the heart to favorite them.
                    </p>

                    <Link href="/favorites">
                        <a className="text-2xl text-primary-500 uppercase font-bold hover:underline">
                            Start Searching
                        </a>
                    </Link>
                </div>
            ) : (
                <div className="mt-8 flex justify-center">
                    <Link href="/favorites">
                        <a className="text-2xl text-primary-500 uppercase font-bold hover:underline">
                            Continue Searching
                        </a>
                    </Link>
                </div>
            )}
        </div>
    );
};

const MyRightFitTools = ({ user }) => {
    return (
        <div className="max-w-2xl flex flex-col space-y-4 text-base divide-y divide-gray-500">
            <div className="flex items-center space-x-8">
                <p className="w-72 font-black uppercase">Assisted Living Assessment</p>

                {user.submittedAssessment ? (
                    <div className="flex items-center space-x-8">
                        <ToolLink href="/resources/assessment">Preview</ToolLink>
                        <ToolLink href="/resources/assessmentr-pdf">Download PDF</ToolLink>
                    </div>
                ) : (
                    <ToolLink href="/resources/assessment">Start</ToolLink>
                )}
            </div>

            <div className="pt-4 flex items-center space-x-8">
                <p className="w-72 font-black uppercase">Assisted Living Preferences</p>

                {user.submittedLivingPreferences ? (
                    <div className="flex items-center space-x-8">
                        <ToolLink href="/resources/living-preferences">Preview</ToolLink>
                        <ToolLink href="/resources/living-preferences-pdf">
                            Download PDF
                        </ToolLink>
                    </div>
                ) : (
                    <ToolLink href="/resources/living-preferences">Start</ToolLink>
                )}
            </div>

            <div className="pt-4 flex items-center space-x-8">
                <p className="w-72 font-black uppercase">Assisted Living Timeline</p>

                {user.submittedTimeline ? (
                    <div className="flex items-center space-x-8">
                        <ToolLink href="/resources/timeline">Preview</ToolLink>
                        <ToolLink href="/resources/timeline-pdf">Download PDF</ToolLink>
                    </div>
                ) : (
                    <ToolLink href="/resources/timeline">Start</ToolLink>
                )}
            </div>

            <div className="pt-4 flex items-center space-x-8">
                <p className="w-72 font-black uppercase">
                    Assisted Living Financial Calculator
                </p>

                {user.submittedFinancialCalculator ? (
                    <div className="flex items-center space-x-8">
                        <ToolLink href="/resources/financial-calculator">Preview</ToolLink>
                        <ToolLink href="/resources/financial-calculator-pdf">
                            Download PDF
                        </ToolLink>
                    </div>
                ) : (
                    <ToolLink href="/resources/financial-calculator">Start</ToolLink>
                )}
            </div>
        </div>
    );
};

const ToolLink = ({ href, children }) => {
    return (
        <Link href={href}>
            <a className="px-4 py-1 text-sm font-black uppercase rounded text-primary-500 hover:bg-gray-200">
                {children}
            </a>
        </Link>
    );
};

export default MyAccountPage;
